import { useState, useCallback, useEffect } from 'react';
import { ContentProps } from 'models/profile-settings.model';

interface UserInfoHookProps {
  firstName?: string;
  lastName?: string;
  preferredFirstName: string;
  description?: string;
  onlyPreferredName?: boolean;
  section?: string;
  onSave?: (values: Record<string, string>) => void;
  onInputChange?: (id: string, value: string) => void;
}

export const useUserInfo = ({
  firstName,
  lastName,
  description,
  preferredFirstName,
  onlyPreferredName = false,
  onInputChange,
  onSave,
}: UserInfoHookProps) => {
  const [contents, setContents] = useState<ContentProps[]>([]);

  useEffect(() => {
    const newContents: ContentProps[] = [
      {
        labelId: 'STR_FIRST_NAME',
        value: firstName ?? '',
        type: 'text',
        isDisabled: true,
      },
      {
        labelId: 'STR_LAST_NAME',
        value: lastName ?? '',
        type: 'text',
        isDisabled: true,
      },
      {
        labelId: description
          ? 'STR_PREFERRED_FIRST_NAME_OPTIONAL'
          : 'STR_PREFERRED_FIRST_NAME',
        value: preferredFirstName,
        type: 'text',
      },
    ];

    const filteredContents = onlyPreferredName
      ? newContents.filter(
          (content) =>
            content.labelId === 'STR_PREFERRED_FIRST_NAME' ||
            content.labelId === 'STR_PREFERRED_FIRST_NAME_OPTIONAL',
        )
      : newContents;

    setContents(filteredContents);
  }, [firstName, lastName, preferredFirstName, onlyPreferredName, description]);

  const handleSave = useCallback(
    (editValues: Record<string, string>) => {
      // Update contents state for all fields
      setContents((prevContents) =>
        prevContents.map((content) => ({
          ...content,
          value: editValues[content.labelId] ?? content.value,
        })),
      );

      Object.entries(editValues).forEach(([labelId, value]) => {
        if (
          onlyPreferredName &&
          labelId !== 'STR_PREFERRED_FIRST_NAME' &&
          labelId !== 'STR_PREFERRED_FIRST_NAME_OPTIONAL'
        ) {
          return;
        }
        onInputChange?.(labelId, value);
      });

      onSave?.(editValues);
    },
    [onSave, onInputChange, onlyPreferredName],
  );

  return { contents, handleSave };
};
